import { all, call, put, select, takeEvery } from 'redux-saga/effects'
import Auth from '@aws-amplify/auth'

import MembersActions, {
  getAccess,
  getACMAPI,
  getAllActiveMembers,
  getAllMembers,
  getMemberDetail,
  getSharedMembers,
  getTotalSleepSessions,
  memberSetState,
} from './actions'
import { analytics, http, notification } from '../../services/index'
import { newAPIPaths } from '../../config'
import { GET_CLINICS } from '../clinic/sagas'

// Defined constant function to get user object stored in Redux Store
const getUser = (state) => state.user
const getClinic = (state) => state.clinic

// API call to add a New Member under the logged In Provider or Incase of Super Admin, add a New Member, Provider or Super Admin
export const addMember = async (payload, memberType) => {
  const apiPath =
    payload.user_type === 'home_health_patient'
      ? newAPIPaths.createHomeHealthPatient
      : newAPIPaths.createUser

  if (payload.user_type === 'home_health_patient') {
    const [clinicId] = payload.clinic_ids
    payload.clinic_id = clinicId
  }
  return http
    .post(
      apiPath,
      payload,
      { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` },
      'newAPIDomain',
    )
    .then((res) => {
      notification.success(`Success`, `${memberType} created successfully`)
      return { res }
    })
    .catch((err) => {
      const {
        response: {
          data: { message },
        },
      } = err
      notification.error(`Something went wrong`, message || `Please contact support!`)
      return { err }
    })
}

// API call to update the details of a Specific member as a Provider, and in case of SuperAdmin, Provider and Superadmins also
export const updateMember = async (payload, memberType) =>
  http
    .put(
      newAPIPaths.updateUser.replace('{user_id}', payload.user_id),
      payload,
      { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` },
      'newAPIDomain',
    )
    .then((res) => {
      notification.success(`Success`, `${memberType} updated successfully`)
      return { res }
    })
    .catch((err) => {
      const {
        response: {
          data: { message },
        },
      } = err
      notification.error(`Something went wrong`, message || `Please contact support!`)
      return { err }
    })

// API call to get delete a specific member registered under the logged in Provider
export const deleteMember = async (payload) =>
  http
    .delete(
      newAPIPaths.deleteUser.replace('{user_id}', payload.user_id),
      payload,
      { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` },
      'newAPIDomain',
    )
    .then((res) => {
      notification.success(`Success`, `User deleted successfully`)
      return { res }
    })
    .catch((err) => {
      const {
        response: {
          data: { message },
        },
      } = err
      notification.error(`Something went wrong`, message || `Please contact support!`)
      return { err }
    })

export const resetPasswordAction = async (payload) =>
  http
    .put(
      newAPIPaths.resetPasswordAPI.replace('{user_id}', payload.user_id),
      { password: payload.password },
      { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` },
      'newAPIDomain',
    )
    .then((res) => {
      notification.success(`Success`, `Password Reset successfully`)
      return { res }
    })
    .catch((err) => {
      console.log('err', err)
      if (err.response) {
        const {
          response: {
            data: { message },
          },
        } = err
        notification.error(`Something went wrong`, message || `Please contact support!`)
      }

      return { err }
    })

// API call to update the Status of a specific member registered under the logged in provider.
export const setStatus = async (payload) => {
  http
    .put(
      newAPIPaths.updateUserStatus.replace('{user_id}', payload.user_id),
      payload,
      { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` },
      'newAPIDomain',
    )
    .then((res) => {
      notification.success(`Success`, `User updated successfully`)
      return { res }
    })
    .catch((err) => {
      const {
        response: {
          data: { message },
        },
      } = err
      notification.error(`Something went wrong`, message || `Please contact support!`)
      return { err }
    })
}

const getUserListV2 = async (payload = {}) =>
  http
    .get(
      newAPIPaths.getAllUserV2,
      payload,
      {
        Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}`,
      },
      'newAPIDomain',
    )
    .then((res) => ({
      res,
    }))
    .catch((err) => {
      const {
        response: {
          data: { message },
        },
      } = err
      notification.error(`Something went wrong`, message || `Please contact support!`)
      return { err }
    })

const getUserDetail = async (payload) =>
  http
    .get(
      newAPIPaths.getOneUser.replace('{user_id}', payload.user_id),
      {},
      {
        Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}`,
      },
      'newAPIDomain',
    )
    .then((res) => ({
      res: res.data,
    }))
    .catch((err) => {
      const {
        response: {
          data: { message },
        },
      } = err
      notification.error(`Something went wrong`, message || `Please contact support!`)
      return { err }
    })

const getUserLoginHistory = async (payload) =>
  http
    .get(
      newAPIPaths.getOneUserLoginHistory.replace('{user_id}', payload.user_id),
      {},
      {
        Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}`,
      },
      'newAPIDomain',
    )
    .then((res) => ({
      res: res.data,
    }))
    .catch((err) => {
      const {
        response: {
          data: { message },
        },
      } = err
      notification.error(`Something went wrong`, message || `Please contact support!`)
      return { err }
    })

export const getUserClinics = async (payload) =>
  http
    .get(
      newAPIPaths.getOneUserClinics.replace('{user_id}', payload.user_id),
      {},
      { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` },
      'newAPIDomain',
    )
    .then((res) => ({ res: res.data }))
    .catch((err) => {
      const {
        response: {
          data: { message },
        },
      } = err
      notification.error(`Something went wrong`, message || `Please contact support!`)
      return { err }
    })

export const updateUserClinics = async (payload) =>
  http
    .put(
      newAPIPaths.updateUserClinics.replace('{user_id}', payload.user_id),
      { clinic_ids: payload.clinic_ids },
      { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` },
      'newAPIDomain',
    )
    .then((res) => {
      notification.success(`Success`, `Facilities access updated successfully`)
      return {
        res: res.data,
      }
    })
    .catch((err) => {
      const {
        response: {
          data: { message },
        },
      } = err
      notification.error(`Something went wrong`, message || `Please contact support!`)
      return { err }
    })

export const assignFacilities = async (payload) =>
  http
    .put(
      newAPIPaths.assignUsersClinics,
      payload,
      { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` },
      'newAPIDomain',
    )
    .then((res) => {
      notification.success(`Success`, `Facilities assigned successfully`)
      return {
        res: res.data,
      }
    })
    .catch((err) => {
      const {
        response: {
          data: { message },
        },
      } = err
      notification.error(`Something went wrong`, message || `Please contact support!`)
      return { err }
    })

// Function to get Profile Data of a specific Member from the Database and store it in Redux Store.
export function* GET_MEMBER_DETAIL({ payload }) {
  const { memberID } = payload
  yield put(memberSetState({ loading: true }))

  const memberDetail = yield call(getMemberDetail, memberID)
  yield put(memberSetState({ loading: false, memberDetail, redirect: false }))
  return true
}

export function* GET_USER_LIST({ payload = {} }) {
  const {
    page = 1,
    pageSize = 10,
    name = '',
    email = '',
    user_id: userId = '',
    userTypes = [
      'clinic',
      'sa',
      'cip_users',
      'clinical-triage',
      'home_health_patient',
      'field_technician',
    ],
  } = payload

  yield put(
    memberSetState({
      userListLoading: true,
      userList: [],
    }),
  )
  const { clinicsListMap } = yield select(getClinic)

  const { res, err } = yield call(getUserListV2, {
    user_types: userTypes,
    name,
    email,
    page,
    page_size: pageSize,
    user_id: userId,
  })

  if (err) {
    return yield put(
      memberSetState({
        userListLoading: false,
        userList: [],
        userListPagination: null,
      }),
    )
  }

  const users = res.users || []
  const pagination = res.pagination || {}

  // Helper function to add clinic_list to users
  const addClinicList = (user) => {
    if (!user.clinic_ids || !Array.isArray(user.clinic_ids) || user.clinic_ids.length === 0) {
      return {
        ...user,
        clinic_list: [],
      }
    }

    const clinicList = []
    for (let i = 0; i < user.clinic_ids.length; i += 1) {
      const clinic = clinicsListMap[user.clinic_ids[i]]
      if (clinic) {
        clinicList.push(clinic)
      }
    }

    return {
      ...user,
      clinic_list: clinicList,
    }
  }

  const userList = users.map(addClinicList)

  yield put(
    memberSetState({
      userListLoading: false,
      userList,
      userListPagination: pagination,
      userListFilters: {
        page,
        pageSize,
        name,
        email,
        user_id: userId,
        userTypes,
      },
    }),
  )
}

export function* GET_PCC_MEMBERS({ payload = {} }) {
  const { page = 1, pageSize = 50, name = '', email = '', user_id: userId = '' } = payload

  yield put(
    memberSetState({
      pccMembersLoading: true,
      pccMembers: [],
    }),
  )

  const { clinicsListMap } = yield select(getClinic)

  const { res, err } = yield call(getUserListV2, {
    user_types: ['pcc-member'],
    name,
    email,
    page,
    page_size: pageSize,
    user_id: userId,
  })

  if (err) {
    return yield put(
      memberSetState({
        pccMembersLoading: false,
        pccMembers: [],
        pccMembersPagination: null,
      }),
    )
  }

  const users = res.users || []
  const pagination = res.pagination || {}

  const addClinicList = (user) => {
    const clinicList = []
    if (user.clinic_ids && Array.isArray(user.clinic_ids) && user.clinic_ids.length > 0) {
      for (let i = 0; i < user.clinic_ids.length; i += 1) {
        const clinic = clinicsListMap[user.clinic_ids[i]]
        if (clinic) {
          clinicList.push(clinic)
        }
      }
    }
    return {
      ...user,
      clinic_list: clinicList,
    }
  }

  const userList = users.map(addClinicList)

  yield put(
    memberSetState({
      pccMembersLoading: false,
      pccMembers: userList,
      pccMembersPagination: pagination,
      pccMembersFilters: {
        page,
        pageSize,
        name,
        email,
        user_id: userId,
      },
    }),
  )
}

export function* GET_COMPASSUS_MEMBERS({ payload = {} }) {
  const { page = 1, pageSize = 50, name = '', email = '', user_id: userId = '' } = payload

  yield put(
    memberSetState({
      compassusMembersLoading: true,
      compassusMembers: [],
    }),
  )

  const { clinicsListMap } = yield select(getClinic)

  const { res, err } = yield call(getUserListV2, {
    user_types: ['compassus-sso-member'],
    name,
    email,
    page,
    page_size: pageSize,
    user_id: userId,
  })

  if (err) {
    return yield put(
      memberSetState({
        compassusMembersLoading: false,
        compassusMembers: [],
        compassusMembersPagination: null,
      }),
    )
  }

  const users = res.users || []
  const pagination = res.pagination || {}

  const addClinicList = (user) => {
    const clinicList = []
    if (user.clinic_ids && Array.isArray(user.clinic_ids) && user.clinic_ids.length > 0) {
      for (let i = 0; i < user.clinic_ids.length; i += 1) {
        const clinic = clinicsListMap[user.clinic_ids[i]]
        if (clinic) {
          clinicList.push(clinic)
        }
      }
    }
    return {
      ...user,
      clinic_list: clinicList,
    }
  }

  const userList = users.map(addClinicList)

  yield put(
    memberSetState({
      compassusMembersLoading: false,
      compassusMembers: userList,
      compassusMembersPagination: pagination,
      compassusMembersFilters: {
        page,
        pageSize,
        name,
        email,
        user_id: userId,
      },
    }),
  )
}

export function* GET_USER_DETAIL({ payload }) {
  yield put(
    memberSetState({
      userDetailLoading: true,
      userDetail: {},
    }),
  )
  let clinicsListMap
  const { clinicsListMap: clinicsListMapFromState } = yield select(getClinic)
  clinicsListMap = clinicsListMapFromState
  if (Object.keys(clinicsListMap).length === 0) {
    yield call(GET_CLINICS)
    const { clinicsListMap: clinicsListMapFromStateAgain } = yield select(getClinic)
    clinicsListMap = clinicsListMapFromStateAgain
  }
  const { res: userDetail, err } = yield call(getUserDetail, payload)
  if (err || !userDetail) {
    return yield put(
      memberSetState({
        userDetailLoading: false,
        userDetail: {},
      }),
    )
  }
  userDetail.clinic_list = userDetail.clinic_ids
    ? userDetail.clinic_ids.map((clinicID) => clinicsListMap[clinicID])
    : []
  yield put(memberSetState({ userDetailLoading: false, userDetail }))
}

export function* GET_USER_LOGIN_HISTORY({ payload }) {
  yield put(
    memberSetState({
      userLoginHistoryLoading: true,
      userLoginHistory: [],
    }),
  )
  const { res, err } = yield call(getUserLoginHistory, payload)
  if (err) {
    return yield put(
      memberSetState({
        userLoginHistoryLoading: false,
        userLoginHistory: [],
      }),
    )
  }
  const authEvents = (res && res.data && res.data.AuthEvents) || (res && res.AuthEvents) || []
  yield put(memberSetState({ userLoginHistoryLoading: false, userLoginHistory: authEvents }))
}

export function* GET_USER_CLINICS({ payload }) {
  yield put(
    memberSetState({
      loadingInTransferClinicModal: true,
      userClinics: [],
    }),
  )
  const { res: userClinics, err } = yield call(getUserClinics, payload)
  if (err) {
    return yield put(
      memberSetState({
        loadingInTransferClinicModal: false,
        userClinics: [],
      }),
    )
  }
  yield put(memberSetState({ loadingInTransferClinicModal: false, userClinics }))
}

export function* UPDATE_USER_CLINICS({ payload }) {
  yield put(
    memberSetState({
      loadingInTransferClinicModal: true,
      facilityAssignedStatus: 'please wait!',
    }),
  )
  const { err } = yield call(updateUserClinics, payload)
  if (err) {
    return yield put(
      memberSetState({
        loadingInTransferClinicModal: false,
        facilityAssignedStatus: 'failed',
      }),
    )
  }
  const { userListFilters, userListPagination } = yield select((state) => state.member)
  const refreshPayload = userListFilters || {}
  if (userListPagination && userListPagination.current_page) {
    refreshPayload.page = userListPagination.current_page
    refreshPayload.pageSize = userListPagination.page_size
  }
  yield call(GET_USER_LIST, { payload: refreshPayload })
  yield put(
    memberSetState({ loadingInTransferClinicModal: false, facilityAssignedStatus: 'success' }),
  )
}

export function* UPDATE_USER_CLINICS_PCC_MEMBERS({ payload }) {
  yield put(
    memberSetState({
      loadingInTransferClinicModal: true,
      facilityAssignedStatus: 'please wait!',
    }),
  )
  const { err } = yield call(updateUserClinics, payload)
  if (err) {
    return yield put(
      memberSetState({
        loadingInTransferClinicModal: false,
        facilityAssignedStatus: 'failed',
      }),
    )
  }
  const { pccMembersFilters, pccMembersPagination } = yield select((state) => state.member)
  const refreshPayload = pccMembersFilters || {}
  if (pccMembersPagination && pccMembersPagination.current_page) {
    refreshPayload.page = pccMembersPagination.current_page
    refreshPayload.pageSize = pccMembersPagination.page_size
  }
  yield call(GET_PCC_MEMBERS, { payload: refreshPayload })
  yield put(
    memberSetState({ loadingInTransferClinicModal: false, facilityAssignedStatus: 'success' }),
  )
}

export function* ASSIGN_FACILITIES({ payload }) {
  yield call(assignFacilities, payload)
  const { userListFilters, userListPagination } = yield select((state) => state.member)
  const refreshPayload = userListFilters || {}
  if (userListPagination && userListPagination.current_page) {
    refreshPayload.page = userListPagination.current_page
    refreshPayload.pageSize = userListPagination.page_size
  }
  yield call(GET_USER_LIST, { payload: refreshPayload })
}

export function* ASSIGN_FACILITIES_PCC_MEMBERS({ payload }) {
  yield call(assignFacilities, payload)
  const { pccMembersFilters, pccMembersPagination } = yield select((state) => state.member)
  const refreshPayload = pccMembersFilters || {}
  if (pccMembersPagination && pccMembersPagination.current_page) {
    refreshPayload.page = pccMembersPagination.current_page
    refreshPayload.pageSize = pccMembersPagination.page_size
  }
  yield call(GET_PCC_MEMBERS, { payload: refreshPayload })
}

export function* UPDATE_USER_CLINICS_COMPASSUS_MEMBERS({ payload }) {
  yield put(
    memberSetState({
      loadingInTransferClinicModal: true,
      facilityAssignedStatus: 'please wait!',
    }),
  )
  const { err } = yield call(updateUserClinics, payload)
  if (err) {
    return yield put(
      memberSetState({
        loadingInTransferClinicModal: false,
        facilityAssignedStatus: 'failed',
      }),
    )
  }
  const { compassusMembersFilters, compassusMembersPagination } = yield select(
    (state) => state.member,
  )
  const refreshPayload = compassusMembersFilters || {}
  if (compassusMembersPagination && compassusMembersPagination.current_page) {
    refreshPayload.page = compassusMembersPagination.current_page
    refreshPayload.pageSize = compassusMembersPagination.page_size
  }
  yield call(GET_COMPASSUS_MEMBERS, { payload: refreshPayload })
  yield put(
    memberSetState({ loadingInTransferClinicModal: false, facilityAssignedStatus: 'success' }),
  )
}

export function* ASSIGN_FACILITIES_COMPASSUS_MEMBERS({ payload }) {
  yield call(assignFacilities, payload)
  const { compassusMembersFilters, compassusMembersPagination } = yield select(
    (state) => state.member,
  )
  const refreshPayload = compassusMembersFilters || {}
  if (compassusMembersPagination && compassusMembersPagination.current_page) {
    refreshPayload.page = compassusMembersPagination.current_page
    refreshPayload.pageSize = compassusMembersPagination.page_size
  }
  yield call(GET_COMPASSUS_MEMBERS, { payload: refreshPayload })
}

// Function to get Profile Data of a specific Provider from the Database and store it in Redux Store.
export function* GET_PROVIDER_PROFILE({ payload }) {
  yield put(memberSetState({ providerMembersloading: true }))
  const { admin_id: memberID } = payload
  const providerDetail = yield call(getMemberDetail, memberID)
  yield put(memberSetState({ providerDetail }))
  yield call(GET_PROVIDER_MEMBERS, { payload: { data: payload } })

  return true
}

// Function to get a List of all the Members Registered under a specific Provider and to be stored in provider Variables of Redux Store
export function* GET_PROVIDER_MEMBERS({ payload }) {
  const { role } = yield select(getUser)
  const { data } = payload
  const sharedData = { coach_id: data.admin_id }
  let providerMembers = yield call(getAllMembers, data, role)
  providerMembers = providerMembers.data
  let providerActiveMembers = yield call(getAllActiveMembers, data, role)
  providerActiveMembers = providerActiveMembers.data
  const providerTotalSleepSessions = yield call(getTotalSleepSessions, data, role)
  let providerSharedMembers = yield call(getSharedMembers, sharedData, role)
  providerSharedMembers = providerSharedMembers.data
  yield put(
    memberSetState({
      providerMembersloading: false,
      providerActiveMembers,
      providerMembers,
      providerSharedMembers,
      providerTotalSleepSessions,
    }),
  )

  return {
    providerMembersloading: false,
    providerActiveMembers,
    providerMembers,
    providerSharedMembers,
    providerTotalSleepSessions,
  }
}

// Function to get a List of all the Members Registered under a specific Provider and to be stored in the Main Variables of Redux Store
export function* GET_MEMBERS({ payload }) {
  const { role, details } = yield select(getUser)
  const { clinicsList } = yield select(getClinic)
  let { data } = payload
  let { filteredType } = data
  yield put(
    memberSetState({
      loading: true,
      lastFormValues: {},
    }),
  )

  if (!data) {
    data = {}
  }
  const sharedData = {}
  if (role !== 'Superadmin') {
    data.admin_id = details.user_id
    sharedData.coach_id = details.user_id
  }
  if (!filteredType) {
    filteredType = ''
  }
  let members = yield call(getAllMembers, data, role)

  members = members.data
  const clinicAdmins = members.filter((item) => item.user_type === 'clinic')
  const superAdmin = members.filter((item) => item.user_type === 'sa')
  members = clinicAdmins.map((item) => {
    item.clinic_list = item.clinic_ids.map(
      (clinicID) => clinicsList.filter((subItem) => subItem.id === clinicID)[0],
    )
    return item
  })
  members = [...members, ...superAdmin]
  const payloadToBeSent = {
    loading: false,
    members,
    filteredType,
    redirect: false,
  }

  // Saving allMembers in another variable in Reducer to prevent overwriting data on Filter

  if (data.vcstatus) {
    payloadToBeSent.allMembers = members
  }

  yield put(memberSetState(payloadToBeSent))

  return true
}

export function* RESET_PASSWORD({ payload }) {
  yield put(memberSetState({ loading: true }))
  yield call(resetPasswordAction, payload)
  yield put(memberSetState({ loading: false }))
}

// Function to add a new Member, Provider or SuperAdmin to the Database and Refresh the members list in Redux store
export function* ADD_NEW_MEMBER({ payload }) {
  const { data, memberView } = payload
  yield put(
    memberSetState({
      formLoading: true,
      redirect: false,
      newMembers: 'please wait',
    }),
  )
  const userTypes = {
    sa: 'sa',
    'clinic-web': 'clinic',
    'clinic-app': 'clinic',
    clinic: 'clinic',
    cip_users: 'cip_users',
    'clinical-triage': 'clinical-triage',
    home_health_patient: 'home_health_patient',
    field_technician: 'field_technician',
  }
  const callArray = data.keys.map((key) => {
    const isAppUser = data.user_type[key] === 'clinic-app'
    const isHomeHealthUser = data.user_type[key] === 'home_health_patient'
    const userData = {
      first_name: data.first_name[key],
      last_name: data.last_name[key],
      email: data.email[key],
      password: data.password[key],
      dob: data.dob[key],
      gender: data.gender[key],
      is_otp_enabled: data.is_otp_enabled[key],
      weight_kg: data.weight_kg,
      height_cm: data.height_cm,
      user_type: userTypes[data.user_type[key]],
      is_cpro_app_member: isAppUser,
      clinic_ids: isAppUser || isHomeHealthUser ? [data.clinic_id[key]] : data.clinic_id[key],
      groups: data.groups[key],
      m_credentials: data.m_credentials[key],
      m_npi: data.m_npi[key],
    }
    if (data.contact[key]) {
      userData.contact = data.selected_code[key] + data.contact[key]
    }
    return call(addMember, userData, memberView)
  })

  yield all(callArray)

  yield put(memberSetState({ formLoading: false, redirect: true, newMembers: 'success' }))

  yield call(GET_USER_LIST, { payload: {} })

  analytics.event({
    action: 'add_admin',
    category: 'user_dir',
    label: 'Submit',
    value: { data, memberView },
  })

  return true
}

// Function to update the data of a Specific Member and Refresh the members list in Redux Store
export function* UPDATE_MEMBER({ payload }) {
  const { data, memberView } = payload
  yield put(memberSetState({ formLoading: true, redirect: false, memberUpdated: 'please wait' }))
  const { res } = yield call(updateMember, data, memberView)
  if (res) {
    yield call(GET_USER_LIST, { payload: {} })
  }
  yield put(memberSetState({ formLoading: false, redirect: true, memberUpdated: 'success' }))
  analytics.event({
    action: 'update_admin',
    category: 'user_dir',
    label: 'Submit',
    value: { data, memberView },
  })
  return true
}

// Function to update the status of the member and Refresh the members list in Redux Store
export function* SET_STATUS({ payload }) {
  yield put(memberSetState({ setStatusLoading: true }))
  yield call(setStatus, payload)
  const { userListFilters, userListPagination } = yield select((state) => state.member)
  const refreshPayload = userListFilters || {}
  if (userListPagination && userListPagination.current_page) {
    refreshPayload.page = userListPagination.current_page
    refreshPayload.pageSize = userListPagination.page_size
  }
  yield call(GET_USER_LIST, { payload: refreshPayload })
  yield put(memberSetState({ setStatusLoading: false }))
}

// Function to delete a member from the database and Refresh the members list in Redux Store
export function* DELETE_MEMBER({ payload }) {
  yield put(memberSetState({ memberDeleted: 'please wait' }))
  const { data, memberView } = payload
  yield call(deleteMember, data, memberView)
  yield call(GET_USER_LIST, { payload: {} })
  yield put(memberSetState({ redirect: false, memberDeleted: 'success' }))

  analytics.event({
    action: 'delete_resident',
    category: 'resident_dir',
    label: 'Delete Resident',
    value: { data, memberView },
  })

  return true
}

// Function to get all the API's available on Server
export function* GET_ACM_APIS() {
  yield put(
    memberSetState({
      acmLoading: true,
      acmAPIs: [],
      saAccess: [],
      vcAdminAccess: [],
      memberAccess: [],
    }),
  )

  const acmAPIs = yield call(getACMAPI)
  const saAccess = yield call(getAccess, 'sa')
  const vcAdminAccess = yield call(getAccess, 'vc-admin')
  const memberAccess = yield call(getAccess, 'member')
  yield put(
    memberSetState({
      acmLoading: false,
      acmAPIs,
      saAccess,
      vcAdminAccess,
      memberAccess,
      redirect: false,
    }),
  )
}

// Assigning constant Strings to Saga Functions, in order to access Saga Functions from Redux
export default function* rootSaga() {
  yield all([
    takeEvery(MembersActions.GET_MEMBER_DETAIL, GET_MEMBER_DETAIL),
    takeEvery(MembersActions.GET_MEMBERS, GET_MEMBERS),
    takeEvery(MembersActions.ADD_NEW_MEMBER, ADD_NEW_MEMBER),
    takeEvery(MembersActions.UPDATE_MEMBER, UPDATE_MEMBER),
    takeEvery(MembersActions.SET_STATUS, SET_STATUS),
    takeEvery(MembersActions.DELETE_MEMBER, DELETE_MEMBER),
    takeEvery(MembersActions.GET_ACM_APIS, GET_ACM_APIS),
    takeEvery(MembersActions.GET_PROVIDER_PROFILE, GET_PROVIDER_PROFILE),
    takeEvery(MembersActions.GET_USER_LIST, GET_USER_LIST),
    takeEvery(MembersActions.GET_USER_DETAIL, GET_USER_DETAIL),
    takeEvery(MembersActions.GET_USER_LOGIN_HISTORY, GET_USER_LOGIN_HISTORY),
    takeEvery(MembersActions.GET_USER_CLINICS, GET_USER_CLINICS),
    takeEvery(MembersActions.UPDATE_USER_CLINICS, UPDATE_USER_CLINICS),
    takeEvery(MembersActions.UPDATE_USER_CLINICS_PCC_MEMBERS, UPDATE_USER_CLINICS_PCC_MEMBERS),
    takeEvery(MembersActions.ASSIGN_FACILITIES, ASSIGN_FACILITIES),
    takeEvery(MembersActions.RESET_PASSWORD, RESET_PASSWORD),
    takeEvery(MembersActions.GET_PCC_MEMBERS, GET_PCC_MEMBERS),
    takeEvery(MembersActions.GET_COMPASSUS_MEMBERS, GET_COMPASSUS_MEMBERS),
    takeEvery(MembersActions.ASSIGN_FACILITIES_PCC_MEMBERS, ASSIGN_FACILITIES_PCC_MEMBERS),
    takeEvery(MembersActions.UPDATE_USER_CLINICS_COMPASSUS_MEMBERS, UPDATE_USER_CLINICS_COMPASSUS_MEMBERS),
    takeEvery(MembersActions.ASSIGN_FACILITIES_COMPASSUS_MEMBERS, ASSIGN_FACILITIES_COMPASSUS_MEMBERS),
  ])
}
